<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, PropType, inject } from 'vue'
import DeparturesService from '../../services/departures-service'
import { Terminal } from './../../types/enums'
import { AppSettingsKey } from './../../types/keys'
import type {
  AppSettings,
  DepartureBoards,
  DepartureSettings,
} from '../../types/'
import { handleLoadingState } from './../../utilities/async-helper'
import { getLocaleDateWithTimeZone } from '../../utilities/date-helper'
import { getCurrentCulture } from './../../utilities/culture-helper'
import DepartureSearch from './DepartureSearch.vue'
import DeparturesList from './DeparturesList.vue'
import LoadingWrapper from '../shared/LoadingWrapper.vue'

const props = defineProps({
  terminal: {
    type: String as PropType<Terminal>,
    required: true,
  },
  maxResults: {
    type: Number,
    default: 9,
  },
})

const { apiBaseUrl, resourceStrings } = inject(AppSettingsKey) as AppSettings

const loadingState = ref<boolean>(true)
const mounted = ref<boolean>(false)
const errorState = ref<boolean>(false)
const pauseMainRefesh = ref<boolean>(false)
const currentCulture = getCurrentCulture()
const currentFilter = ref<string | null>(null)
const departuresService = new DeparturesService(apiBaseUrl)

const refreshSeconds = computed<number>(
  () => settings.value?.refreshRateSeconds || 10
)

const hasDepartures = computed<boolean>(() => {
  if (departureBoards.value?.departures) {
    return departureBoards.value.departures.length > 0
  }
  return false
})

let mainRefreshInterval: number
let filterRefreshInternal: number
let departureBoards = ref<DepartureBoards>()
let settings = ref<DepartureSettings | null>()

async function fetchDepartures(): Promise<void> {
  await handleLoadingState(loadingState, errorState, async () => {
    const departuresResponse = await departuresService.GetDepartures(
      props.terminal,
      props.maxResults
    )
    settings.value = departuresResponse.settings
    departureBoards.value = departuresResponse.departureBoards
  })
}

async function searchDepartures(): Promise<void> {
  await handleLoadingState(loadingState, errorState, async () => {
    const departureSearchResult = await departuresService.SearchDepartures(
      props.terminal,
      currentFilter.value || ''
    )
    if (departureSearchResult) {
      departureBoards.value = departureSearchResult
    } else {
      // API down?
      console.error(
        'Departures search endpoint returned a null or empty response. API may be down.'
      )
    }
  })
}

onMounted(async () => {
  await fetchDepartures()
  mounted.value = true

  // Refresh the main departure boards list
  mainRefreshInterval = window.setInterval(async () => {
    if (!pauseMainRefesh.value) {
      await fetchDepartures()
    }
  }, refreshSeconds.value * 1000)

  // Refresh filtered results if applicable
  filterRefreshInternal = window.setInterval(async () => {
    if (currentFilter.value && pauseMainRefesh.value) {
      await searchDepartures()
    }
  }, refreshSeconds.value * 1000)
})

onUnmounted(() => {
  clearInterval(mainRefreshInterval)
  clearInterval(filterRefreshInternal)
})

async function handleDepartureSearch(term: string): Promise<void> {
  // halt main timer, store ref to term and kick off search
  pauseMainRefesh.value = true
  currentFilter.value = term

  // Kick off a search
  await searchDepartures()
}

async function handleDepartureSearchCleared(): Promise<void> {
  // Reset flags/state
  pauseMainRefesh.value = false
  currentFilter.value = null

  // Re-fetch main listing
  await fetchDepartures()
}
</script>
<template>
  <div class="bg p-b-x16 departure-board-wrap">
    <div v-if="errorState && !departureBoards" class="context-err">
      <svg class="context-err__icon"><use xlink:href="#svg-warning"></use></svg>
      {{ resourceStrings['TravelUpdates.Departures.ApiErrorMessage'] }}
    </div>
    <LoadingWrapper :loading="!mounted">
      <div v-if="settings" class="departure-board" data-departure-board="">
        <template v-if="settings.enabled">
          <div class="departure-board__title">
            {{ settings.title[getCurrentCulture(false).cultureShortCode] }}
          </div>
          <div
            v-if="
              settings.introduction[getCurrentCulture(false).cultureShortCode]
            "
            class="departure-board__intro"
          >
            {{
              settings.introduction[getCurrentCulture(false).cultureShortCode]
            }}
          </div>
          <DepartureSearch
            v-if="departureBoards?.showDepartureBoards"
            @onDepartureSearch="handleDepartureSearch"
            @onSearchCleared="handleDepartureSearchCleared"
          />
        </template>
        <div class="departures-wrap">
          <template v-if="settings.enabled">
            <div
              class="departure-board__no-results"
              v-if="!hasDepartures && currentFilter"
            >
              {{
                resourceStrings[
                  'TravelUpdates.SearchResults.SorryNoMatchesPrefix'
                ]
              }}
              <strong>{{ currentFilter }}</strong
              >.
              {{
                resourceStrings[
                  'TravelUpdates.SearchResults.SorryNoMatchesSuffix'
                ]
              }}
            </div>
            <div
              v-if="
                departureBoards?.lastUpdatedVisible &&
                departureBoards?.showDepartureBoards
              "
              class="departure-board__last-updated"
            >
              {{ resourceStrings['TravelUpdates.Labels.LastUpdated'] }}:
              {{ getLocaleDateWithTimeZone(departureBoards.lastUpdated) }}
            </div>
            <DeparturesList
              v-if="
                departureBoards?.showDepartureBoards &&
                departureBoards?.departures
              "
              :current-culture="currentCulture"
              :departures="departureBoards.departures"
              :settings="settings"
            />
          </template>
        </div>
      </div>
    </LoadingWrapper>
  </div>
</template>

<style lang="scss" scoped>
.departures-wrap,
.departure-board-wrap {
  position: relative;
  min-height: 400px;
}

.departure-board__no-results {
  margin: 0 0 48px;
  font-size: 24px;
}
</style>
